import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { listMyOrganizations, stampBrowserRequestContext } from '@pc/db';
import {
  BrandLockup,
  Button,
  Drawer,
  IdleLock,
  ReadOnlyBanner,
  Rule,
  ToastProvider,
  bindToastsToBus,
  useIsMobile,
  useToast,
} from '@pc/ui';
import { appBus } from '@pc/core';
import { useRouteTracking } from '@pc/analytics-client';
import { FooterLanguagePicker } from '@pc/i18n';
import { useAuth } from '../auth/AuthProvider';
import { useActiveContext } from '../auth/ActiveContextProvider';
import { usePlatformAdmin } from '../admin/usePlatformAdmin';
import { env } from '../env';
import { TrialBanner } from './TrialBanner';
import {
  operatorShellAsideClass,
  operatorShellFrameClass,
  operatorShellGridClass,
  operatorShellMainClass,
  operatorShellMainColumnClass,
  operatorShellNavClass,
} from './operatorShellLayout';
import { setReporterActiveContext } from './getActiveContextForReporter';
import { useChooseStoreThen } from '../org/useChooseStoreThen';
import { useModules } from './useModules';
import { DevModeBanner } from '../devmode/DevModeBanner';
import { DevModeDock } from '../devmode/DevModeDock';
import { DevModeSidebarEntry } from '../devmode/DevModeSidebarEntry';
import {
  MobileTabBar,
  HomeIcon,
  MenuIcon,
  iconForRoute,
  type MobileTab,
} from './MobileTabBar';
import { GlobalSearch } from './GlobalSearch';

/**
 * Slim app shell rendered when `useActiveContext().mode === 'org'`.
 *
 * Mirrors {@link AppShell} but with a hardcoded org-mode nav (Dashboard,
 * Calendar, Clients, Orders, Settings) instead of the per-module
 * registry. Used by the "Enter organization" entry point on
 * `/choose-context`.
 *
 * Org-mode pages are read-only pivots over multiple stores; clicking a
 * row deep-links into a specific store (which flips `activeMode` back
 * to 'store') for create / edit flows. See `apps/web/src/org/*`.
 */
export function OrgAppShell() {
  return (
    <ToastProvider>
      <div className={operatorShellFrameClass}>
        <OrgShellInner />
      </div>
    </ToastProvider>
  );
}

function OrgShellInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, signOut } = useAuth();
  const { activeOrgId, mode, clear } = useActiveContext();
  const toast = useToast();
  const adminQ = usePlatformAdmin();
  const orgsQ = useQuery({
    queryKey: ['shell', 'my-orgs'],
    queryFn: listMyOrganizations,
    staleTime: 5 * 60_000,
    enabled: Boolean(activeOrgId),
  });
  const orgName = orgsQ.data?.find((o) => o.id === activeOrgId)?.name ?? null;
  const [navOpen, setNavOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const modules = useModules();
  const productsEnabled = modules.some((m) => m.id === 'products');
  const messagingEnabled = modules.some((m) => m.id === 'messaging');
  // The footer's "Enter a store" button now drops the operator straight
  // into the chosen store's dashboard ('/') rather than bouncing back to
  // the picker. Single-store orgs skip the modal entirely; multi-store
  // orgs see the same picker the in-page "+ New ..." buttons use, so the
  // muscle memory is shared.
  const { trigger: chooseStoreThen, modal: chooseStoreModal } =
    useChooseStoreThen();

  useRouteTracking({ paused: !session });

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  useEffect(() => {
    void stampBrowserRequestContext(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setReporterActiveContext({
      userId: session?.user.id ?? null,
      userEmail: session?.user.email ?? null,
      orgId: activeOrgId ?? null,
      storeId: null,
    });
  }, [session?.user.id, session?.user.email, activeOrgId]);

  // Bind toasts to the in-app event bus so other modules can `appBus().emit('app.toast', ...)`
  // and have us render them. Mirrors AppShell.
  useEffect(() => {
    const bus = appBus();
    const off = bindToastsToBus(toast.push, (evt, h) =>
      bus.on(evt as never, h as never),
    );
    return () => {
      off();
    };
  }, [toast]);

  function enterTheStore() {
    // Hands off to the same store picker the in-page "+ New ..." buttons
    // use, so the operator's interaction model is uniform: org-mode
    // buttons all flow through `useChooseStoreThen` -> single-store
    // orgs skip the modal, multi-store orgs see the picker, and the
    // chosen store becomes active before the destination route renders.
    chooseStoreThen('/');
  }

  const sidebarContent: ReactNode = (
    <>
      <div className="shrink-0 px-5 pt-6 pb-4">
        <a
          href="/"
          className="block mb-3 leading-none"
          aria-label="PeptideClients home"
        >
          <img
            src="https://bxbdqmtldkhgwpnwecox.supabase.co/storage/v1/object/public/Public%20Photos/LOGOUpdated.png"
            alt="PeptideClients"
            width="128"
            height="32"
            className="h-8 w-auto block"
          />
        </a>
        <p className="display text-lg">{t('org.shell.title')}</p>
      </div>

      <nav className={operatorShellNavClass}>
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
          }
        >
          {t('org.shell.nav.dashboard')}
        </NavLink>
        <Rule className="my-2" />
        <NavLink
          to="/calendar"
          className={({ isActive }) =>
            `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
          }
        >
          {t('org.shell.nav.calendar')}
        </NavLink>
        <NavLink
          to="/clients"
          className={({ isActive }) =>
            `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
          }
        >
          {t('org.shell.nav.clients')}
        </NavLink>
        <NavLink
          to="/orders"
          className={({ isActive }) =>
            `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
          }
        >
          {t('org.shell.nav.orders')}
        </NavLink>
        {productsEnabled ? (
          <NavLink
            to="/products"
            className={({ isActive }) =>
              `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
            }
          >
            {t('org.shell.nav.products')}
          </NavLink>
        ) : null}
        {messagingEnabled ? (
          <NavLink
            to="/messages"
            className={({ isActive }) =>
              `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
            }
          >
            {t('org.shell.nav.messages')}
          </NavLink>
        ) : null}
        <Rule className="my-2" />
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `py-2 text-sm ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
          }
        >
          {t('org.shell.nav.settings')}
        </NavLink>
        {adminQ.data ? (
          <>
            <Rule className="my-2" />
            <p className="smallcap mb-1">{t('org.shell.platformAdmin')}</p>
            <DevModeSidebarEntry onNavigate={() => setNavOpen(false)} />
            {env.adminConsoleUrl ? (
              <a
                href={env.adminConsoleUrl}
                target="_blank"
                rel="noreferrer"
                className="py-2 text-sm text-muted hover:text-ink"
              >
                {t('org.shell.adminConsole')} <span aria-hidden>↗</span>
              </a>
            ) : null}
          </>
        ) : null}
      </nav>

      <div className="shrink-0 px-5 py-4 border-t border-rule flex flex-col gap-2">
        <p className="truncate text-sm text-ink" title={orgName ?? undefined}>
          {orgName ?? '—'}
        </p>
        <p className="text-[10px] text-muted uppercase tracking-wider">
          {t('org.shell.footer.modeOrg')}
        </p>
        <Button size="sm" variant="ghost" onClick={enterTheStore}>
          {t('org.shell.footer.enterStore')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            clear();
            navigate('/choose-context');
          }}
        >
          {t('org.shell.footer.switchWorkspace')}
        </Button>
        <Button size="sm" variant="subtle" onClick={signOut}>
          {t('actions.signOut')}
        </Button>
      </div>
    </>
  );

  const mobileTabs: MobileTab[] = [
    { key: 'home', to: '/', end: true, label: t('app.mobileNav.home'), icon: HomeIcon },
    { key: '/calendar', to: '/calendar', label: t('org.shell.nav.calendar'), icon: iconForRoute('/calendar') },
    { key: '/clients', to: '/clients', label: t('org.shell.nav.clients'), icon: iconForRoute('/clients') },
    { key: '/orders', to: '/orders', label: t('org.shell.nav.orders'), icon: iconForRoute('/orders') },
    { key: 'menu', label: t('app.mobileNav.menu'), icon: MenuIcon, onClick: () => setNavOpen(true) },
  ];

  // Defensive: if the mode flipped to 'store' while this shell is mounted
  // (e.g. user clicked a deep-link), the router will rebuild and unmount us,
  // but in case of a race surfacing here, we render the same skeleton.
  if (mode !== 'org') {
    return null;
  }

  return (
    <div className={operatorShellGridClass}>
      <aside
        className={operatorShellAsideClass}
        onWheel={(e) => e.stopPropagation()}
      >
        {sidebarContent}
      </aside>

      <header className="md:hidden sticky top-0 z-30 bg-paper/90 backdrop-blur border-b border-rule">
        <div className="flex items-center justify-between gap-3 px-4 h-14 pt-[env(safe-area-inset-top)]">
          <button
            type="button"
            aria-label={t('org.shell.openNavigation')}
            aria-expanded={navOpen}
            aria-controls="org-mobile-nav"
            onClick={() => setNavOpen(true)}
            className="-ml-2 p-2 text-ink"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
              className="w-6 h-6"
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <BrandLockup app="backOffice" />
          <GlobalSearch enableShortcut={false} />
          <Button size="sm" variant="ghost" onClick={signOut}>
            {t('actions.signOut')}
          </Button>
        </div>
      </header>

      <Drawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        side="left"
        ariaLabel={t('org.shell.drawerLabel')}
        widthClassName="w-[18rem] md:hidden"
      >
        {sidebarContent}
      </Drawer>

      <div className={operatorShellMainColumnClass}>
        <main className={operatorShellMainClass}>
          <TrialBanner />
          {/* Read-only banner for `viewer` members — see AppShell. */}
          <ReadOnlyBanner />
          <IdleLock appKey="web" onSignOut={signOut} />
          <DevModeBanner />
          <DevModeDock />
          <div className="sticky top-0 z-30 hidden justify-end border-b border-rule bg-paper/95 px-4 py-2 backdrop-blur md:flex md:px-10">
            <GlobalSearch />
          </div>
          <div className="flex flex-col px-4 py-6 md:px-10 md:py-10 md:max-w-[1200px]">
            {/* Remount on org change — see AppShell for the full rationale. */}
            <Outlet key={activeOrgId ?? ''} />
            <footer className="shrink-0 border-t border-rule mt-12 pt-6 text-xs text-muted pb-[env(safe-area-inset-bottom)]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p>{t('app.footer.copyright')}</p>
                <p>
                  <a
                    href="https://docs.peptideclients.com/terms"
                    className="hover:underline"
                  >
                    {t('app.footer.terms')}
                  </a>{' '}
                  ·{' '}
                  <a
                    href="https://docs.peptideclients.com/privacy"
                    className="hover:underline"
                  >
                    {t('app.footer.privacy')}
                  </a>{' '}
                  ·{' '}
                  <a
                    href="https://docs.peptideclients.com/research-use-only"
                    className="hover:underline"
                  >
                    {t('app.footer.researchUseOnly')}
                  </a>
                </p>
                <FooterLanguagePicker />
              </div>
            </footer>
          </div>
        </main>

        {/* App-like bottom navigation (phones only) — see AppShell. */}
        <MobileTabBar tabs={mobileTabs} ariaLabel={t('app.mobileNav.label')} />
      </div>

      {chooseStoreModal}
    </div>
  );
}
